"use client";

import { c as _c } from "react/compiler-runtime";
import { Device, type DeviceInfo } from "@capacitor/device";
import { PushNotifications, type PushNotificationSchema } from "@capacitor/push-notifications";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/navigation";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { getUserPageTabByRoute } from "@/components/user/layout/userTabs.config";
import { type ApiIdentity } from "@/generated/models/ApiIdentity";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import { commonApiPost } from "@/services/api/common-api";
import { useAuth } from "../auth/Auth";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import { getStableDeviceId } from "./stable-device-id";
import type { DevicePushData, PushRedirect } from "./device-push.types";
function parseDevicePushData(raw: unknown): DevicePushData | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const notification_id = o["notification_id"];
  const redirect = o["redirect"];
  const target_profile_id = o["target_profile_id"];
  const target_profile_handle = o["target_profile_handle"];
  if (typeof notification_id !== "string" || typeof redirect !== "string" || typeof target_profile_id !== "string" || typeof target_profile_handle !== "string") return null;
  if (redirect !== "profile" && redirect !== "waves") return null;
  const data: DevicePushData = {
    notification_id,
    redirect: redirect as PushRedirect,
    target_profile_id,
    target_profile_handle
  };
  const handle = o["handle"];
  if (typeof handle === "string") data.handle = handle;
  const subroute = o["subroute"];
  if (subroute === "rep" || subroute === "identity") data.subroute = subroute;
  const wave_id = o["wave_id"];
  if (typeof wave_id === "string") data.wave_id = wave_id;
  const drop_id = o["drop_id"];
  if (typeof drop_id === "string") data.drop_id = drop_id;
  return data;
}
const MAX_REGISTRATION_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 5000;
const IOS_INITIALIZATION_DELAY_MS = 500;
const PROFILE_SWITCH_SETTLE_TIMEOUT_MS = 3000;
const PROFILE_SWITCH_POLL_INTERVAL_MS = 50;
const DELEGATE_ERROR_PATTERNS = ["capacitorDidRegisterForRemoteNotifications", "didRegisterForRemoteNotifications"];
const isDelegateError = (error: unknown): boolean => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return DELEGATE_ERROR_PATTERNS.some(pattern => errorMessage.includes(pattern));
};
const registerWithRetry = async (maxRetries = MAX_REGISTRATION_RETRIES): Promise<void> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await PushNotifications.register();
      return;
    } catch (registerError: unknown) {
      const isDelegate = isDelegateError(registerError);
      const hasRetriesLeft = attempt < maxRetries - 1;
      if (isDelegate && hasRetriesLeft) {
        const delay = Math.min(INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt), MAX_RETRY_DELAY_MS);
        console.warn(`iOS push notification registration attempt ${attempt + 1} failed. Retrying in ${delay}ms...`, registerError);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw registerError;
    }
  }
};
type NotificationsContextType = {
  removeWaveDeliveredNotifications: (waveId: string) => Promise<void>;
  removeAllDeliveredNotifications: () => Promise<void>;
};
const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);
const redirectConfig = {
  path: ({
    path
  }: {
    path: string;
  }) => `/${path}`,
  profile: ({
    handle,
    subroute
  }: {
    handle: string;
    subroute?: string;
  }) => {
    if (!subroute) return `/${handle}`;
    const validTab = getUserPageTabByRoute(subroute);
    if (!validTab) return `/${handle}`;
    return `/${handle}/${validTab.route}`;
  },
  "the-memes": ({
    id
  }: {
    id: string;
  }) => `/the-memes/${id}`,
  "6529-gradient": ({
    id
  }: {
    id: string;
  }) => `/6529-gradient/${id}`,
  "meme-lab": ({
    id
  }: {
    id: string;
  }) => `/meme-lab/${id}`,
  waves: ({
    wave_id,
    drop_id
  }: {
    wave_id: string;
    drop_id: string;
  }) => {
    return getWaveRoute({
      waveId: wave_id,
      serialNo: drop_id || undefined,
      isDirectMessage: false,
      isApp: false
    });
  }
};
export const NotificationsProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(41);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isIos,
    isActive
  } = useCapacitor();
  const {
    connectedProfile
  } = useAuth();
  const {
    address,
    connectedAccounts,
    seizeSwitchConnectedAccount
  } = useSeizeConnectContext();
  const router = useRouter();
  const initializationRef = useRef(null);
  const isRegisteredRef = useRef(false);
  const connectedProfileRef = useRef(connectedProfile);
  const connectedAccountsRef = useRef(connectedAccounts);
  const activeAddressRef = useRef(address);
  let t1;
  let t2;
  if ($[0] !== connectedProfile) {
    t1 = () => {
      connectedProfileRef.current = connectedProfile;
    };
    t2 = [connectedProfile];
    $[0] = connectedProfile;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== connectedAccounts) {
    t3 = () => {
      connectedAccountsRef.current = connectedAccounts;
    };
    t4 = [connectedAccounts];
    $[3] = connectedAccounts;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== address) {
    t5 = () => {
      activeAddressRef.current = address;
    };
    t6 = [address];
    $[6] = address;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== isIos) {
    t7 = async notifications => {
      if (isIos && isRegisteredRef.current) {
        ;
        try {
          await PushNotifications.removeDeliveredNotifications({
            notifications
          });
        } catch (t8) {
          const error = t8;
          console.error("Error removing delivered notifications", error);
        }
      }
    };
    $[9] = isIos;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const removeDeliveredNotifications = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = t9 => {
      const {
        target_profile_id,
        target_profile_handle
      } = t9;
      if (connectedProfileRef.current?.id === target_profile_id && activeAddressRef.current) {
        return activeAddressRef.current;
      }
      const profileMatchedAccount = connectedAccountsRef.current.find(account => account.profileId === target_profile_id);
      if (profileMatchedAccount) {
        return profileMatchedAccount.address;
      }
      const roleMatchedAccount = connectedAccountsRef.current.find(account_0 => account_0.role === target_profile_id);
      if (roleMatchedAccount) {
        return roleMatchedAccount.address;
      }
      const normalizedHandle = target_profile_handle.toLowerCase();
      const handleMatchedAccount = connectedAccountsRef.current.find(account_1 => account_1.profileHandle?.toLowerCase() === normalizedHandle);
      if (handleMatchedAccount) {
        return handleMatchedAccount.address;
      }
      return null;
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const resolveAddressForNotificationProfile = t8;
  let t9;
  if ($[12] !== seizeSwitchConnectedAccount) {
    t9 = async (notificationProfileId, matchedAddress) => {
      const isMatchedProfileActive = () => {
        const activeAddress = activeAddressRef.current;
        if (!activeAddress) {
          return false;
        }
        const normalizedActiveAddress = activeAddress.toLowerCase();
        const normalizedMatchedAddress = matchedAddress.toLowerCase();
        if (normalizedActiveAddress !== normalizedMatchedAddress) {
          return false;
        }
        return connectedProfileRef.current?.id === notificationProfileId;
      };
      const waitForProfileSwitchSettlement = async () => {
        const timeoutAt = Date.now() + PROFILE_SWITCH_SETTLE_TIMEOUT_MS;
        while (Date.now() < timeoutAt) {
          if (isMatchedProfileActive()) {
            return true;
          }
          await new Promise(_temp);
        }
        return isMatchedProfileActive();
      };
      if (isMatchedProfileActive()) {
        return true;
      }
      if (activeAddressRef.current?.toLowerCase() === matchedAddress.toLowerCase()) {
        return await waitForProfileSwitchSettlement();
      }
      ;
      try {
        await Promise.resolve(seizeSwitchConnectedAccount(matchedAddress));
        const didSettle = await waitForProfileSwitchSettlement();
        if (!didSettle) {
          console.warn("Ignoring notification: switched wallet account but profile did not settle in time", {
            notificationProfileId,
            matchedAddress
          });
        }
        return didSettle;
      } catch (t10) {
        const error_0 = t10;
        console.warn("Ignoring notification: failed to switch to matched connected profile", {
          notificationProfileId,
          matchedAddress,
          error: error_0
        });
        return false;
      }
    };
    $[12] = seizeSwitchConnectedAccount;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const switchToMatchedAddress = t9;
  let t10;
  if ($[14] !== removeDeliveredNotifications || $[15] !== switchToMatchedAddress) {
    t10 = async (routerInstance, notification) => {
      const raw = notification.data ?? {};
      const notificationData = parseDevicePushData(raw);
      if (!notificationData) {
        await removeDeliveredNotifications([notification]);
        console.warn("Ignoring notification: invalid payload shape", {
          raw
        });
        return;
      }
      const targetProfileId = notificationData.target_profile_id.trim();
      const targetProfileHandle = notificationData.target_profile_handle.trim();
      if (targetProfileId.length === 0 || targetProfileHandle.length === 0) {
        await removeDeliveredNotifications([notification]);
        console.warn("Ignoring notification: missing target profile metadata", {
          targetProfileId,
          targetProfileHandle
        });
        return;
      }
      const matchedAddress_0 = resolveAddressForNotificationProfile({
        target_profile_id: targetProfileId,
        target_profile_handle: targetProfileHandle
      });
      if (!matchedAddress_0) {
        await removeDeliveredNotifications([notification]);
        console.warn("Ignoring notification: target profile is not one of connected accounts", {
          targetProfileId,
          targetProfileHandle
        });
        return;
      }
      const didSwitch = await switchToMatchedAddress(targetProfileId, matchedAddress_0);
      if (!didSwitch) {
        return;
      }
      await removeDeliveredNotifications([notification]);
      const {
        handle: rawHandle,
        ...notificationDataWithoutHandle
      } = notificationData;
      const handle = typeof rawHandle === "string" ? rawHandle.trim() : "";
      const redirectData = handle ? {
        ...notificationDataWithoutHandle,
        handle
      } : notificationDataWithoutHandle;
      const redirectUrl = resolveRedirectUrl(redirectData);
      if (redirectUrl) {
        routerInstance.push(redirectUrl);
      } else {
        console.warn("No redirect url found in notification data", notificationData);
      }
    };
    $[14] = removeDeliveredNotifications;
    $[15] = switchToMatchedAddress;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handlePushNotificationAction = t10;
  let t11;
  if ($[17] !== handlePushNotificationAction || $[18] !== isIos || $[19] !== router) {
    t11 = async profile => {
      ;
      try {
        isRegisteredRef.current = false;
        await PushNotifications.removeAllListeners();
        const stableDeviceId = await getStableDeviceId();
        const deviceInfo = await Device.getInfo();
        await PushNotifications.addListener("registration", async token => {
          isRegisteredRef.current = true;
          await registerPushNotification(stableDeviceId, deviceInfo, token.value, profile);
        });
        await PushNotifications.addListener("registrationError", error_2 => {
          isRegisteredRef.current = false;
          console.error("Push registration error: ", error_2);
          Sentry.captureException(error_2, {
            tags: {
              component: "NotificationsProvider",
              operation: "pushRegistrationError"
            }
          });
        });
        await PushNotifications.addListener("pushNotificationReceived", _temp2);
        await PushNotifications.addListener("pushNotificationActionPerformed", async action => {
          await handlePushNotificationAction(router, action.notification);
        });
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive === "granted") {
          if (isIos) {
            await new Promise(_temp3);
          }
          await registerWithRetry();
        } else {
          console.warn("Push notifications permission not granted");
        }
      } catch (t12) {
        const error_1 = t12;
        console.error("Error in initializePushNotifications", error_1);
        throw error_1;
      }
    };
    $[17] = handlePushNotificationAction;
    $[18] = isIos;
    $[19] = router;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const initializePushNotifications = t11;
  let t12;
  if ($[21] !== initializePushNotifications || $[22] !== isCapacitor) {
    t12 = async profile_0 => {
      if (isCapacitor) {
        await initializePushNotifications(profile_0);
      }
    };
    $[21] = initializePushNotifications;
    $[22] = isCapacitor;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const initializeNotifications = t12;
  let t13;
  let t14;
  if ($[24] !== connectedProfile || $[25] !== initializeNotifications || $[26] !== isActive || $[27] !== isCapacitor) {
    t13 = () => {
      const profileId = connectedProfile?.id ?? null;
      if (isCapacitor && isActive && initializationRef.current !== profileId) {
        initializationRef.current = profileId;
        initializeNotifications(connectedProfile ?? undefined).catch(error_3 => {
          console.error("Failed to initialize push notifications", error_3);
          Sentry.captureException(error_3, {
            tags: {
              component: "NotificationsProvider",
              operation: "initializeNotifications"
            }
          });
          initializationRef.current = null;
        });
      }
    };
    t14 = [connectedProfile, isCapacitor, isActive, initializeNotifications];
    $[24] = connectedProfile;
    $[25] = initializeNotifications;
    $[26] = isActive;
    $[27] = isCapacitor;
    $[28] = t13;
    $[29] = t14;
  } else {
    t13 = $[28];
    t14 = $[29];
  }
  useEffect(t13, t14);
  let t15;
  if ($[30] !== isIos || $[31] !== removeDeliveredNotifications) {
    t15 = async waveId => {
      if (isIos && isRegisteredRef.current) {
        ;
        try {
          const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
          const waveNotifications = deliveredNotifications.notifications.filter(notification_1 => notification_1.data?.wave_id === waveId);
          await removeDeliveredNotifications(waveNotifications);
        } catch (t16) {
          const error_4 = t16;
          console.error("Error removing wave delivered notifications", error_4);
          Sentry.captureException(error_4, {
            tags: {
              component: "NotificationsProvider",
              operation: "removeWaveDeliveredNotifications"
            }
          });
        }
      }
    };
    $[30] = isIos;
    $[31] = removeDeliveredNotifications;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const removeWaveDeliveredNotifications = t15;
  let t16;
  if ($[33] !== isIos) {
    t16 = async () => {
      if (isIos && isRegisteredRef.current) {
        ;
        try {
          await PushNotifications.removeAllDeliveredNotifications();
        } catch (t17) {
          const error_5 = t17;
          console.error("Error removing all delivered notifications", error_5);
          Sentry.captureException(error_5, {
            tags: {
              component: "NotificationsProvider",
              operation: "removeAllDeliveredNotifications"
            }
          });
        }
      }
    };
    $[33] = isIos;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const removeAllDeliveredNotifications = t16;
  let t17;
  if ($[35] !== removeAllDeliveredNotifications || $[36] !== removeWaveDeliveredNotifications) {
    t17 = {
      removeWaveDeliveredNotifications,
      removeAllDeliveredNotifications
    };
    $[35] = removeAllDeliveredNotifications;
    $[36] = removeWaveDeliveredNotifications;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const value = t17;
  let t18;
  if ($[38] !== children || $[39] !== value) {
    t18 = <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
    $[38] = children;
    $[39] = value;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};
const registerPushNotification = async (deviceId: string, deviceInfo: DeviceInfo, token: string, profile?: ApiIdentity) => {
  try {
    await commonApiPost({
      endpoint: `push-notifications/register`,
      body: {
        device_id: deviceId,
        token,
        platform: deviceInfo.platform,
        profile_id: profile?.id
      }
    });
  } catch (error) {
    console.error("Push registration error", error);
    Sentry.captureException(error, {
      tags: {
        component: "NotificationsProvider",
        operation: "registerPushNotification"
      }
    });
  }
};
const resolveRedirectUrl = (notificationData: DevicePushData) => {
  const {
    redirect,
    ...params
  } = notificationData;
  if (!redirect) {
    console.warn("No redirect type found in notification data", notificationData);
    return null;
  }
  const resolveFn = redirectConfig[redirect];
  if (!resolveFn) {
    console.error("Unknown redirect type", redirect);
    return null;
  }
  try {
    return (resolveFn as (params: Record<string, unknown>) => string)(params);
  } catch (error) {
    console.error("Error resolving redirect URL", error);
    return null;
  }
};
export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotificationsContext must be used within a NotificationsProvider");
  }
  return context;
};
function _temp(resolve) {
  return setTimeout(resolve, PROFILE_SWITCH_POLL_INTERVAL_MS);
}
function _temp2(notification_0) {
  console.warn("Push notification received: ", notification_0);
}
function _temp3(resolve_0) {
  return setTimeout(resolve_0, IOS_INITIALIZATION_DELAY_MS);
}